import { useEffect, useState, useCallback } from "react";
import { Container, Button, Grid, Typography, CardHeader } from "@mui/material";
import { useAuth } from "../../hooks/useAuth";
import { GetTokenIdentity } from "../../utils/Token";
import { Icons } from "../../utils/Icon";

import axios from "axios";
import moment from "moment";
import MaterialTable from "material-table";
import Swal from "sweetalert2";

import EditTouchpointForm from "../../components/forms/touchpoints/Edit";
import CountrySelect from "../../components/common/CountrySelect";

export const ReminderListPage = () => {
    const { token, logout } = useAuth();
    const identity = GetTokenIdentity(token);

    const defaultReminderStartDate = "1900-01-01";
    const defaultReminderEndDate = moment().add(100, "years").format("YYYY-MM-DD");

    const [data, setData] = useState([]);
    const [count, setCount] = useState(0);
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [filter, setFilter] = useState({
        reminder_start_date: defaultReminderStartDate,
        reminder_end_date: defaultReminderEndDate,
        country_id: identity.user.country_id,
        is_completed: 0,
    });
    const [sort, setSort] = useState({ field: "reminder_date", sort: "asc" });

    const [open, setOpen] = useState({ edit: false });
    const [selectedRow, setSelectedRow] = useState({});

    const handleOpen = (type, row) => {
        setOpen({ ...open, [type]: true });
        setSelectedRow(row);
    };

    const handleClose = (type) => {
        setOpen({ ...open, [type]: false });
        setSelectedRow(null);
    };

    const handleResetFilter = () => {
        setPage(0);
        setFilter({
            reminder_start_date: defaultReminderStartDate,
            reminder_end_date: defaultReminderEndDate,
            country_id: identity.user.country_id,
            is_completed: 0,
        });
    };

    const handleFilterChange = (event) => {
        setPage(0);
        setFilter((prevFilter) => ({ ...prevFilter, [event.target.name]: event.target.value }));
    };

    const handleMarkAsCompleted = async (rowData) => {
        if (rowData?.is_completed === 1) {
            return;
        }

        const confirmation = await Swal.fire({
            title: "Mark as Called / Followed?",
            text: "Are you sure you want to mark this touchpoint as called/followed?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes",
            cancelButtonText: "No",
            reverseButtons: true,
        });

        if (!confirmation.isConfirmed) {
            return;
        }

        try {
            const payload = {
                product_id: rowData.product_id || "",
                contact_date: moment(rowData.contact_date).isValid() ? moment(rowData.contact_date).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"),
                reminder_date: moment(rowData.reminder_date).isValid() ? moment(rowData.reminder_date).format("YYYY-MM-DD") : "",
                is_assigned_to_group: rowData.is_assigned_to_group !== undefined ? rowData.is_assigned_to_group : 0,
                assigned_to: rowData.assigned_to || "",
                remark: (rowData.remark || "").trim(),
                is_completed: 1,
            };

            const response = await axios.patch(process.env.REACT_APP_WEBSITE_BACKEND_URL + `api/v1/touchpoints/${rowData.touchpoint_id}`, payload);

            if (response.status === 200) {
                Swal.fire({
                    title: "Success",
                    text: "Touchpoint marked as called/followed successfully",
                    icon: "success",
                    showConfirmButton: false,
                    timer: 2000,
                });
                fetchData();
            }
        } catch (error) {
            if (error.response && error.response.status === 401) {
                console.log("Unauthorized: ", error);
                logout("Your account has been logged out of a session.");
            } else {
                Swal.fire({
                    title: "Error",
                    text: error.response?.data?.message || "An error occurred while marking touchpoint as called/followed. Please try again later.",
                    icon: "error",
                });
            }
        }
    };

    const fetchData = useCallback(async () => {
        try {
            const { data } = await axios.get(process.env.REACT_APP_WEBSITE_BACKEND_URL + `api/v1/touchpoints`, {
                params: {
                    page: page + 1,
                    perPage,
                    filter: JSON.stringify(filter),
                    sort: JSON.stringify(sort),
                },
            });
            setData(data.data.touchpoints);
            setCount(data.data.total);
        } catch (error) {
            if (error.response && error.response.status === 401) {
                console.log("Unauthorized: ", error);
                logout("Your account has been logged out of a session.");
            } else {
                console.log(error);
            }
        }
    }, [page, perPage, filter, sort, logout]);

    useEffect(() => {
        fetchData();
    }, [fetchData, page, perPage, filter, sort]);

    const columns = [
        {
            title: "Reminder Date",
            field: "reminder_date",
            align: "center",
            cellStyle: { width: 150, maxWidth: 150 },
            headerStyle: { width: 150, maxWidth: 150 },
            render: (rowData) => moment(rowData.reminder_date).format("YYYY-MM-DD"),
        },
        {
            title: "Organization",
            field: "org_name",
            width: "35%",
        },
        {
            title: "Office Phone",
            field: "office_phone",
            cellStyle: { width: 150, maxWidth: 150 },
            headerStyle: { width: 150, maxWidth: 150 },
            render: (rowData) => rowData.office_phone || "-",
        },
        {
            title: "Mobile Phone",
            field: "mobile_phone",
            cellStyle: { width: 150, maxWidth: 150 },
            headerStyle: { width: 150, maxWidth: 150 },
            render: (rowData) => rowData.mobile_phone || "-",
        },
        {
            title: "Remark",
            field: "remark",
            width: "40%",
        },
        {
            title: "Product",
            field: "product_description",
            width: "25%",
            render: (rowData) => rowData.product_description || "-",
        },
    ];

    const actions = [];

    actions.push({
        icon: () => <Icons.Check />,
        tooltip: "Mark as Called / Followed",
        onClick: (event, rowData) => handleMarkAsCompleted(rowData),
    });

    actions.push({
        icon: () => <Icons.Edit />,
        tooltip: "Edit Touchpoint",
        onClick: (event, rowData) => handleOpen("edit", rowData),
    });

    return (
        <Container maxWidth="xl" style={{ padding: 6 }}>
            <MaterialTable
                title="Touchpoints List"
                icons={Icons}
                columns={columns}
                data={data}
                page={page}
                totalCount={count}
                style={{ padding: "0px" }}
                options={{
                    pageSize: perPage,
                    pageSizeOptions: [10, 20, 30, 50],
                    search: false,
                    filtering: false,
                    sorting: false,
                    actionsColumnIndex: -1,
                    headerStyle: {
                        fontWeight: "bold",
                        backgroundColor: "#f1f1f1",
                        color: "#212122",
                    },
                    maxBodyHeight: "600px",
                    paginationType: "stepped",
                    padding: "dense",
                    draggable: false,
                    rowStyle: (rowData) => ({
                        backgroundColor: selectedRow === rowData.tableData.id ? "#EEE" : "#FFF",
                    }),
                }}
                localization={{
                    pagination: {
                        labelRowsPerPage: "",
                        labelDisplayedRows: "{from}-{to} of {count}",
                        labelRowsSelect: "rows",
                    },
                    toolbar: {
                        nRowsSelected: "{0} row(s) selected",
                    },
                    header: {
                        actions: "",
                    },
                    body: {
                        emptyDataSourceMessage: "No data to display",
                        filterRow: {
                            filterTooltip: "Filter",
                        },
                    },
                }}
                actions={actions}
                onChangePage={(nextPage) => setPage(nextPage)}
                onChangeRowsPerPage={(nextPerPage) => setPerPage(nextPerPage)}
                onRowClick={(evt, row) => setSelectedRow(row.tableData.id)}
                components={{
                    Toolbar: () => (
                        <>
                            <div>
                                <Grid container spacing={2} sx={{ px: 1.6, mt: 0, pb: 1.2 }}>
                                    <Grid item xs={12} sm={12}>
                                        <CardHeader
                                            sx={{ p: 0 }}
                                            title={
                                                <Typography component="h1" variant="h5" color="primary" fontWeight="bold">
                                                    Reminder List
                                                </Typography>
                                            }
                                        />
                                    </Grid>
                                </Grid>
                            </div>
                            <form
                                style={{
                                    borderStyle: "solid",
                                    borderWidth: "thin",
                                    borderColor: "#e1e1e1",
                                }}
                            >
                                <Grid container spacing={2} sx={{ padding: 1.4, paddingTop: 1.9 }}>
                                    <Grid item xs={12} sm={10} md={10.3} xl={10.3}>
                                        <CountrySelect value={filter.country_id === undefined ? "" : filter.country_id} onChange={handleFilterChange} />
                                    </Grid>
                                    <Grid item xs={12} sm={2} md={1.7} xl={1.7}>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            color="error"
                                            sx={{
                                                color: "#ffffff",
                                                marginTop: "2px",
                                            }}
                                            onClick={handleResetFilter}
                                        >
                                            Reset
                                        </Button>
                                    </Grid>
                                </Grid>
                            </form>
                        </>
                    ),
                }}
            />
            <EditTouchpointForm open={open.edit} handleClose={() => handleClose("edit")} rowData={selectedRow} organizationData={selectedRow} fetchData={fetchData} />
        </Container>
    );
};
